class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');

    const gen =
      typeof window.mtBeginCartSync === 'function' ? window.mtBeginCartSync() : Date.now();
    const req = typeof window.mtNextCartReq === 'function' ? window.mtNextCartReq() : Date.now();
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    // Fetch full cart in parallel with painting sections so totals land with the new lines.
    const cartPromise = fetch(`${root}cart.js`).then((res) => res.json());

    this.paintFromParsedSections(parsedState);
    if (typeof window.mtRevealCartItemPrices === 'function') {
      window.mtRevealCartItemPrices();
    }
    if (
      typeof window.mtLockPaintedTotal === 'function' &&
      !document.querySelector('[bss-b2b-cart-total-price]')
    ) {
      window.mtLockPaintedTotal(gen);
    }
    if (typeof window.mtSyncSPSwitcher === 'function') {
      window.mtSyncSPSwitcher();
    }
    if (typeof window.mtFlushPendingCart === 'function') {
      window.mtFlushPendingCart();
    }

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.open();

    const applyCart = (cart) => {
      if (typeof window.mtIsCartSyncCurrent === 'function' && !window.mtIsCartSyncCurrent(gen)) {
        return;
      }
      if (cart.item_count) this.classList.remove('is-empty');
      else this.classList.add('is-empty');
      if (typeof window.mtSyncCartTotals === 'function') {
        window.mtSyncCartTotals(cart, gen, req);
      }
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-render',
        cartData: cart,
      });
    };

    // If cart.js already resolved, apply in this turn (no extra frame of stale total).
    cartPromise.then(applyCart).catch(() => {});
  }

  paintFromParsedSections(parsedState) {
    if (!(parsedState && parsedState.sections && parsedState.sections['cart-drawer'])) return;
    const html = new DOMParser().parseFromString(
      parsedState.sections['cart-drawer'],
      'text/html'
    );
    const liveInner = this.querySelector('.drawer__inner');
    const nextInner = html.querySelector('.drawer__inner');
    if (liveInner && nextInner) {
      liveInner.replaceWith(nextInner);
    } else {
      ['cart-drawer-items', '.cart-drawer__footer', '[data-free-shipping-bar]'].forEach(
        (selector) => {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        }
      );
    }
    if (parsedState.sections['cart-icon-bubble']) {
      const iconHtml = new DOMParser().parseFromString(
        parsedState.sections['cart-icon-bubble'],
        'text/html'
      );
      const liveIcon = document.getElementById('cart-icon-bubble');
      const nextIcon = iconHtml.getElementById('cart-icon-bubble');
      if (liveIcon && nextIcon) liveIcon.replaceWith(nextIcon);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
